/**
 * PublicFooter — the ONE footer for every unauthenticated page.
 *
 * Replaces three: `VFooter.js` (dark, newsletter + policies — legal and auth
 * pages), the inline `Footer()` in `pages/landing_page.js` (bone, Explore grid)
 * and the inline `Footer()` in `pages/seo/SeoLandingPage.js` (white, Explore
 * grid). Each knew about links the other two did not, so the landing page had
 * no route to the privacy policy and the legal pages had no route to any of the
 * fifteen marketing pages.
 *
 * ── Why ink, and not the bone it used to be ──────────────────────────────────
 * The logo is a shopfront living inside a browser window, with a hanging OPEN
 * sign. This is the bottom of the page: the shop closing for the day. Ink ground,
 * one lit window in it. That is the reason, and it also solves a real problem —
 * on bone, fifteen dark 14px links sat at the SAME contrast as the brand and won
 * by sheer count. On ink they can be held at ~6:1 while the brand line sits at
 * 16:1, so "quiet" is a measurable fact rather than a hope.
 *
 * ── The signature ────────────────────────────────────────────────────────────
 * ONE white window card (INIKRU_UI's `.win-card`, hand-rolled here in Tailwind
 * so `landing_page.js`'s local `.win-pop` override cannot reach it) with the
 * hard offset shadow in CREAM instead of ink — an ink offset is invisible on an
 * ink ground. It holds the single CTA and the real phone number and email. It is
 * the only bright object down here; everything else is deliberately dim.
 *
 * ── What was removed, and why ────────────────────────────────────────────────
 * The newsletter form. It was not a newsletter: it POSTed to `/api/contact_us/`
 * with the body "Please add <email> to the INIKRU newsletter list", i.e. it filed
 * a support ticket. It also put a second submit button directly beside the one
 * CTA that matters. If real list signup is wanted it should be a real endpoint,
 * not a contact message wearing a costume.
 *
 * 🔴 The link grid is DERIVED from `SEO_NAV_GROUPS` — never hand-type this list.
 * Grouping comes from each page's `navGroup` and the anchor text from its
 * `navLabel`, so a page renamed or removed in `seoPages.js` cannot leave a stale
 * link behind. This grid is also what makes the fifteen-page cluster crawlable
 * from every public page: drop it and they become orphans.
 *
 * 🔴 No competitor's brand name renders here. The one page that compares against
 * one links as "Why switch to Inikru". Enforced at the registry (`navLabel`),
 * which is the only place this footer takes words from — so it cannot be
 * reintroduced by editing this file.
 */
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { FaInstagram, FaXTwitter, FaFacebookF, FaLinkedinIn, FaPinterestP } from 'react-icons/fa6';
import { INIKRU_LOGO_WHITE as inikruLogoWhite } from '../utils/brandAssets';
import { SEO_NAV_GROUPS } from '../pages/seo/seoPages';

// Two focus treatments, because the ring needs an offset that matches what is
// BEHIND it: the ink ground out here, white paper inside the window card.
const FOCUS =
  'focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]';
const FOCUS_ON_PAPER =
  'focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B40A0A]';

const PHONE_E164 = '+919704387878';
const PHONE_HUMAN = '+91 97043 87878';
const EMAIL = 'team@inikru.com';
const WHATSAPP =
  'https://wa.me/919704387878?text=' +
  encodeURIComponent("Hi! I'd like to know more about Inikru.");

// Paths match App.js EXACTLY. VFooter linked `/TermsNConditions`; the route is
// `/termsNconditions` and only React Router's case-insensitive default kept it
// off a 404.
const LEGAL = [
  ['Terms of service', '/termsNconditions'],
  ['Privacy policy', '/privacyPolicy'],
  ['Payments & shipping', '/payShip'],
  ['Returns & exchange', '/rtnPolicy'],
  ['FAQs', '/Faqs'],
  ['Contact us', '/contactus'],
];

const SOCIALS = [
  { href: 'https://www.instagram.com/inikru/', icon: <FaInstagram />, label: 'Instagram' },
  { href: 'https://x.com/inikru', icon: <FaXTwitter />, label: 'X' },
  { href: 'https://www.facebook.com/inikru', icon: <FaFacebookF />, label: 'Facebook' },
  { href: 'https://www.linkedin.com/company/inikru/', icon: <FaLinkedinIn />, label: 'LinkedIn' },
  { href: 'https://in.pinterest.com/inikru/', icon: <FaPinterestP />, label: 'Pinterest' },
];

// The three lines are not decoration: they are the same three verbs the link
// groups below are filed under (Build / Run / Sell). The footer's structure and
// its headline say the same thing, so the grid reads as an index to the sentence
// rather than as a sitemap that happens to be nearby.
const CREED = ['Build the store.', 'Run the business.', 'Sell everywhere.'];

export default function PublicFooter() {
  return (
    // The top hairline is for ONE case: `SeoLandingPage` ends with its own ink
    // closing-CTA band, so on those fifteen pages ink meets ink and the footer
    // would otherwise have no beginning. Over the bone pages (landing, legal,
    // auth) white-at-10%-over-ink is indistinguishable from the ink itself, so
    // it costs those pages nothing.
    <footer className="border-t border-white/10 bg-ink text-white">
      {/* ── Brand + the one lit window ─────────────────────────────────────── */}
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-14 lg:grid-cols-12 lg:gap-16 lg:px-10 lg:py-20">
        <div className="lg:col-span-7">
          <Link to="/" aria-label="Inikru home" className={`inline-flex items-center rounded-[6px] ${FOCUS}`}>
            <img src={inikruLogoWhite} alt="Inikru" className="h-6 w-auto" />
          </Link>

          <p className="mt-7 font-display text-[27px] font-extrabold leading-[1.12] tracking-[-0.025em] text-bone sm:text-[34px] lg:text-[44px]">
            {CREED.map((line) => <span key={line} className="block">{line}</span>)}
          </p>

          <p className="mt-6 max-w-[52ch] text-[15.5px] leading-relaxed text-white/65">
            One login for your online store, your stock, your bills and your customers —
            instead of four tools that disagree with each other.
          </p>

          <div className="mt-8 flex gap-2.5">
            {SOCIALS.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className={`grid h-9 w-9 place-items-center rounded-full border border-white/20 text-white/60 transition-colors hover:border-white/70 hover:text-white ${FOCUS}`}
              >
                {s.icon}
              </a>
            ))}
          </div>
        </div>

        {/* The window card. Cream offset shadow = the logo's hard shadow, read as
            warm light on a dark ground. ONE per view, per INIKRU_UI. */}
        <div className="lg:col-span-5 lg:justify-self-end">
          <div className="w-full overflow-hidden rounded-[12px] bg-white shadow-[7px_7px_0_0_#F5E3DC] lg:w-[380px]">
            <div className="flex items-center gap-1.5 border-b-[1.5px] border-ink bg-bone px-3.5 py-2.5">
              <span className="h-2 w-2 rounded-full bg-primary" />
              <span className="h-2 w-2 rounded-full bg-ink" />
              <span className="h-2 w-2 rounded-full border border-ink/20 bg-cream" />
            </div>

            <div className="p-6">
              <p className="font-display text-[15px] font-extrabold tracking-[-0.01em] text-ink">
                Ready when you are.
              </p>

              <div className="mt-4 flex items-center gap-4">
                <a
                  href="/Register"
                  className={`group inline-flex items-center justify-center gap-2 rounded-[10px] bg-primary px-5 py-3 font-display text-[14px] font-bold text-white transition-colors hover:bg-primary-dark ${FOCUS_ON_PAPER}`}
                >
                  Start free
                  <ArrowRight size={16} className="transition-transform motion-safe:group-hover:translate-x-0.5" />
                </a>
                <Link
                  to="/login"
                  className={`rounded-[6px] text-[14px] font-bold text-gray-600 transition-colors hover:text-ink ${FOCUS_ON_PAPER}`}
                >
                  Sign in
                </Link>
              </div>

              <div className="mt-6 border-t border-line pt-5">
                <p className="font-display text-[10.5px] font-bold uppercase tracking-[0.18em] text-gray-500">
                  Talk to us
                </p>
                <ul className="mt-3 space-y-2 text-[14px]">
                  <li>
                    <a href={`mailto:${EMAIL}`} className={`rounded-[6px] font-semibold text-ink transition-colors hover:text-primary ${FOCUS_ON_PAPER}`}>
                      {EMAIL}
                    </a>
                  </li>
                  <li>
                    <a href={`tel:${PHONE_E164}`} className={`rounded-[6px] font-semibold text-ink transition-colors hover:text-primary ${FOCUS_ON_PAPER}`}>
                      {PHONE_HUMAN}
                    </a>
                  </li>
                  <li>
                    <a
                      href={WHATSAPP}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`rounded-[6px] text-gray-600 transition-colors hover:text-primary ${FOCUS_ON_PAPER}`}
                    >
                      Message us on WhatsApp
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── The marketing pages, kept quiet ────────────────────────────────────
          Group headings carry the meaning, so there is no parent "Explore"
          label to read past. Two columns at 390px: every label is short enough
          to sit on one line at that width, so nothing wraps into a ragged mess
          and nothing overflows.

          The column count tracks the number of GROUPS, not the number of links
          — it is 4 because `SEO_NAV_GROUPS` yields four (Build / Run / Sell /
          Compare). The two industry pages opt out via NOT_IN_FOOTER, so 13 of
          the 15 registry entries render here. Add or remove a group and this
          number needs revisiting; a stale one leaves an empty column. */}
      <div className="border-t border-white/10">
        <nav aria-label="More from Inikru" className="mx-auto max-w-7xl px-5 py-12 lg:px-10">
          <div className="grid grid-cols-2 gap-x-6 gap-y-9 md:grid-cols-4 lg:gap-x-8">
            {SEO_NAV_GROUPS.map((group) => (
              <div key={group.title}>
                <p className="font-display text-[10.5px] font-bold uppercase tracking-[0.18em] text-white/50">
                  {group.title}
                </p>
                <ul className="mt-4 space-y-2.5">
                  {group.links.map(({ path, label }) => (
                    <li key={path}>
                      <Link
                        to={path}
                        className={`block rounded-[6px] text-[13.5px] leading-snug text-white/60 transition-colors hover:text-white ${FOCUS}`}
                      >
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </nav>
      </div>

      {/* ── Legal ──────────────────────────────────────────────────────────────
          Everything is packed to the LEFT and the bottom-right corner is left
          deliberately empty. That corner is not ours: `landing_page.js` parks a
          `fixed bottom-5 right-5` "Chat with your expert" launcher there, which
          sits on top of the last ~68px of the page on every route that renders
          it. The old "Back to top" button lived exactly under it and could not
          be clicked at full scroll. It is gone rather than moved — it was the
          least useful thing in the footer, and the Home key already does it.
          The extra bottom padding below `lg` keeps the launcher off the policy
          links once they wrap on a phone. */}
      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 pb-20 pt-7 text-[12.5px] lg:flex lg:items-center lg:gap-x-6 lg:px-10 lg:pb-7">
          <p className="mb-3 text-white/40 lg:mb-0">© {new Date().getFullYear()} INIKRU</p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2.5">
            {LEGAL.map(([label, href]) => (
              <li key={href}>
                <Link to={href} className={`rounded-[6px] text-white/50 transition-colors hover:text-white ${FOCUS}`}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
